import {
  HHStack,
  HHTableCell,
  HHTableRow,
  HHTypography,
} from '@hinge-health/react-component-library';
import { Link } from '@mui/material';
import { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  buildProviderDetailPath,
  PROVIDER_HEALTH_LABELS,
} from '../constants/partner-data-gateway.constant';
import { useBasePath } from '../context/base-path-context';
import { ProviderSummary } from '../domain/types';
import { formatCount } from '../utils/format.utils';
import { getProviderHealth } from '../utils/provider-health.utils';
import { OutcomeStrip, OUTCOME_STRIP_UNAVAILABLE_LABEL } from './outcome-strip';
import { RelativeTimeCell } from './relative-time-cell';
import { StatusChip } from './status-chip';
import { TableCard, tableCardTestIds } from './table-card';

const TEST_ID_PREFIX = 'pdg-providers-table';
const ids = tableCardTestIds(TEST_ID_PREFIX);

export const PROVIDERS_TABLE_TEST_ID = ids.table;
export const PROVIDERS_TABLE_EMPTY_TEST_ID = ids.empty;
export const PROVIDERS_TABLE_SKELETON_TEST_ID = ids.skeleton;
export const PROVIDERS_TABLE_ERROR_TEST_ID = ids.error;
export const PROVIDERS_TABLE_FOOTER_TEST_ID = ids.footer;
export const PROVIDERS_TABLE_TOOLBAR_TEST_ID = ids.toolbar;
export const providerRowTestId = (dataProvider: string): string =>
  `${TEST_ID_PREFIX}-row-${dataProvider}`;

export const PROVIDERS_TABLE_EMPTY_MESSAGE =
  'No providers match this filter. Choose All to see every provider.';
/** For an empty fleet, where no filter is to blame. */
export const PROVIDERS_TABLE_NO_PROVIDERS_MESSAGE =
  'No providers yet. They appear here once a configuration exists or a file arrives.';
export const PROVIDERS_TABLE_ERROR_MESSAGE =
  'Something went wrong loading providers.';

const COLUMN_HEADERS = [
  'Provider',
  'Health',
  'Latest file',
  'Recent files',
  'Loaded',
  'Rejected',
  'Failed',
];

interface ProviderRowProps {
  summary: ProviderSummary;
}

const ProviderRow = ({ summary }: ProviderRowProps): JSX.Element => {
  const basePath = useBasePath();
  const health = getProviderHealth(summary);
  const latest = summary.latestIngestion;
  const { completed, partial, rejected, failed } = summary.recentCounts;

  return (
    <HHTableRow hover data-testid={providerRowTestId(summary.dataProvider)}>
      <HHTableCell>
        <HHStack gap={0.5}>
          <Link
            component={RouterLink}
            to={`${basePath}/${buildProviderDetailPath(summary.dataProvider)}`}
            underline="hover"
            color="text.primary"
            variant="body2"
            sx={{ fontWeight: 500, wordBreak: 'break-all' }}
          >
            {summary.dataProvider}
          </Link>
          {!summary.hasConfiguration && (
            <HHTypography
              hhVariant="variant-bypass"
              variant="caption"
              color="text.secondary"
            >
              No configuration
            </HHTypography>
          )}
        </HHStack>
      </HHTableCell>
      <HHTableCell>
        <StatusChip status={health} label={PROVIDER_HEALTH_LABELS[health]} />
      </HHTableCell>
      <HHTableCell>
        {latest ? (
          <HHStack gap={0.5}>
            <HHTypography
              hhVariant="variant-bypass"
              variant="body2"
              noWrap
              sx={{
                maxWidth: 240,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {latest.fileName}
            </HHTypography>
            <RelativeTimeCell isoDate={latest.createdAt} />
          </HHStack>
        ) : (
          <HHTypography
            hhVariant="variant-bypass"
            variant="body2"
            color="text.secondary"
          >
            No files yet
          </HHTypography>
        )}
      </HHTableCell>
      <HHTableCell>
        <OutcomeStrip
          outcomes={summary.recentOutcomes}
          label={
            summary.loadError ? OUTCOME_STRIP_UNAVAILABLE_LABEL : undefined
          }
          testId={`${providerRowTestId(summary.dataProvider)}-strip`}
        />
      </HHTableCell>
      <HHTableCell>{formatCount(completed + partial)}</HHTableCell>
      <HHTableCell>{formatCount(rejected)}</HHTableCell>
      <HHTableCell>{formatCount(failed)}</HHTableCell>
    </HHTableRow>
  );
};

interface ProvidersTableProps {
  summaries: ProviderSummary[];
  isLoading?: boolean;
  hasError?: boolean;
  toolbar?: ReactNode;
  /** Defaults to the "no match for this filter" wording. */
  emptyMessage?: string;
}

export const ProvidersTable = ({
  summaries,
  isLoading = false,
  hasError = false,
  toolbar,
  emptyMessage = PROVIDERS_TABLE_EMPTY_MESSAGE,
}: ProvidersTableProps): JSX.Element => (
  <TableCard
    testIdPrefix={TEST_ID_PREFIX}
    ariaLabel="Providers"
    headers={COLUMN_HEADERS}
    count={summaries.length}
    noun={{ singular: 'provider', plural: 'providers' }}
    emptyMessage={emptyMessage}
    errorMessage={PROVIDERS_TABLE_ERROR_MESSAGE}
    isLoading={isLoading}
    hasError={hasError}
    toolbar={toolbar}
  >
    {summaries.map(summary => (
      <ProviderRow key={summary.dataProvider} summary={summary} />
    ))}
  </TableCard>
);
